import { useEffect, useMemo, useState } from 'react';
import { getSettings, putSetting, testVision, type AdminSetting, type VisionProbe } from '../api';
import { useApi } from '../useApi';

/**
 * Friendly editor over the `ai.*` runtime settings (the raw key/value rows also
 * live on the generic Settings page). Values flow through the EXISTING settings
 * API — GET /v1/admin/settings to load, PUT /v1/admin/settings/:key to save —
 * there are no AI-specific endpoints. Secrets (the API key) arrive masked and
 * are written with a "replace" input, reusing the Settings page pattern.
 */

// ---------- setting keys (single source of truth for the field↔key mapping) ----------
const K_PROVIDER = 'ai.vision.provider';
const K_API_KEY = 'ai.vision.api_key';
const K_ENDPOINT = 'ai.vision.endpoint';
const K_MODEL = 'ai.vision.model';
const K_DUPLICATE = 'ai.checks.duplicate.enabled';
const K_NSFW = 'ai.checks.nsfw.enabled';
const K_FACE = 'ai.checks.face.enabled';
const K_GENDER = 'ai.checks.gender.enabled';
const K_AI_GENERATED = 'ai.checks.ai_generated.enabled';
const K_SAME_PERSON = 'ai.checks.same_person.enabled';
const K_NSFW_SELF_HOSTED = 'ai.nsfw.self_hosted';
const K_INHOUSE = 'ai.inhouse.enabled';
const K_INHOUSE_MODELS = 'ai.inhouse.models_url';

type Provider = 'none' | 'anthropic' | 'openai' | 'openai_compatible' | 'minimax';
const PROVIDER_VALUES: Provider[] = ['none', 'anthropic', 'openai', 'openai_compatible', 'minimax'];
const PROVIDERS: { value: Provider; label: string }[] = [
  { value: 'none', label: 'None — moderation off' },
  { value: 'minimax', label: 'MiniMax (low-cost — MiniMax-M3)' },
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'openai_compatible', label: 'OpenAI-compatible (self-hosted / proxy)' },
];

const CHECKS: { key: string; label: string; hint: string }[] = [
  {
    key: K_DUPLICATE,
    label: 'Duplicate detection',
    hint: 'Auto-rejects a photo already used by another account, or the same picture twice. In-house — no provider needed.',
  },
  {
    key: K_NSFW,
    label: 'NSFW / explicit',
    hint: 'Auto-rejects explicit content (needs a vision provider, unless the self-hosted model below is on).',
  },
  {
    key: K_FACE,
    label: 'Real person',
    hint: 'Auto-rejects photos with no real human (cat, object, screenshot). Needs a vision provider.',
  },
  {
    key: K_AI_GENERATED,
    label: 'AI-generated / deepfake',
    hint: 'Auto-rejects images that look AI-generated, rendered, or a deepfake. Needs a vision provider.',
  },
  {
    key: K_SAME_PERSON,
    label: 'Same person across photos',
    hint: 'Auto-rejects a photo that is a different person than the profile’s first photo. Needs a vision provider.',
  },
  {
    key: K_GENDER,
    label: 'Gender match',
    hint: 'Auto-rejects when the photo’s apparent gender doesn’t match the profile (man/woman only; skipped for non-binary). Needs a vision provider.',
  },
];

/** Map raw settings rows → a lookup by key (value for plain rows, hint for secrets). */
function indexSettings(rows: AdminSetting[]): Map<string, AdminSetting> {
  const m = new Map<string, AdminSetting>();
  for (const r of rows) m.set(r.key, r);
  return m;
}

const valueOf = (s: AdminSetting | undefined): string => s?.value ?? '';
const boolOf = (s: AdminSetting | undefined): boolean => valueOf(s) === 'true';

/** A single AI check or self-hosted toggle, persisted as 'true' / 'false'. */
function Toggle({
  label,
  hint,
  checked,
  busy,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  busy: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div className="field" style={{ gap: 6 }}>
      <label className="check" style={{ color: 'var(--text)', fontSize: '0.9rem', textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
        <input
          type="checkbox"
          checked={checked}
          disabled={busy}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
      <span className="hint">{hint}</span>
    </div>
  );
}

export function Ai() {
  const { data, error, loading, reload } = useApi(getSettings);
  const rows = useMemo(() => data?.data ?? [], [data]);
  const byKey = useMemo(() => indexSettings(rows), [rows]);

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  // Provider section drafts (seeded from server values on load / reload).
  const [provider, setProvider] = useState<Provider>('none');
  const [model, setModel] = useState('');
  const [endpoint, setEndpoint] = useState('');
  // The API key is a SECRET — the input is a "replace" field; '' = leave as-is.
  const [apiKey, setApiKey] = useState('');
  // In-house ML (no API) — face-api model folder URL/path.
  const [inhouseModels, setInhouseModels] = useState('');

  // Live connectivity test (runs against the SAVED config, server-side).
  const [probing, setProbing] = useState(false);
  const [probe, setProbe] = useState<VisionProbe | null>(null);

  useEffect(() => {
    if (!data) return;
    const raw = valueOf(byKey.get(K_PROVIDER));
    setProvider((PROVIDER_VALUES.includes(raw as Provider) ? raw : 'none') as Provider);
    setModel(valueOf(byKey.get(K_MODEL)));
    setEndpoint(valueOf(byKey.get(K_ENDPOINT)));
    setInhouseModels(valueOf(byKey.get(K_INHOUSE_MODELS)));
    setApiKey('');
    setProbe(null); // a fresh load may reflect new saved config — old result is stale
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reseed only when fetched data changes
  }, [data]);

  const save = async (key: string, value: string, opts?: { isSecret?: boolean; reloadAfter?: boolean }) => {
    setBusyKey(key);
    setActionError(null);
    setSavedKey(null);
    try {
      await putSetting(key, value, opts?.isSecret);
      setSavedKey(key);
      if (opts?.reloadAfter) reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusyKey(null);
    }
  };

  const runTest = async () => {
    setProbing(true);
    setProbe(null);
    setActionError(null);
    try {
      setProbe(await testVision());
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Test failed.');
    } finally {
      setProbing(false);
    }
  };

  const apiKeySetting = byKey.get(K_API_KEY);
  const apiKeyHint = apiKeySetting?.hint;
  // The test hits SAVED config — warn if the provider was changed but not saved yet.
  const savedProvider = valueOf(byKey.get(K_PROVIDER));
  const providerDirty = provider !== savedProvider;
  // Endpoint is overridable for EVERY provider (use a proxy / self-hosted gateway);
  // blank falls back to the official URL. Only hidden when moderation is off.
  const showEndpoint = provider !== 'none';
  // The two wire-formats differ: Anthropic POSTs the FULL messages URL, OpenAI-style
  // gets a BASE URL we append `/chat/completions` to.
  const endpointPlaceholder =
    provider === 'anthropic'
      ? 'https://your-proxy.example/v1/messages'
      : 'https://your-proxy.example/v1';
  const endpointHint =
    provider === 'anthropic'
      ? 'Full Messages endpoint. Blank = https://api.anthropic.com/v1/messages. For a proxy, include the whole path (…/v1/messages).'
      : provider === 'minimax'
        ? 'Base URL — we append /chat/completions. Blank = https://api.minimaxi.chat/v1. Using a low-cost proxy? Put its base URL here (…/v1).'
        : 'Base URL — we append /chat/completions. Blank = https://api.openai.com/v1. Point this at your proxy or self-hosted gateway.';

  return (
    <div className="admin-wrap">
      <div className="page-head">
        <h1>
          AI moderation<span className="dot">.</span>
        </h1>
      </div>
      <p className="hint" style={{ marginBottom: 4 }}>
        Photos are checked at upload (registration + profile edits). Every enabled check{' '}
        <strong>auto-rejects</strong> — a non-human, NSFW, AI-generated, duplicate, different-person,
        or gender-mismatched photo is refused on the spot and the user is told why and asked to
        replace it. Flagged edge cases still surface in the{' '}
        <a href="/moderation" style={{ color: 'var(--accent)' }}>
          Moderation
        </a>{' '}
        queue. The vision checks need a provider below; duplicate is in-house.
      </p>
      {actionError && <p className="error-line">{actionError}</p>}

      {error ? (
        <div className="table-wrap">
          <div className="empty">{error}</div>
        </div>
      ) : loading && !data ? (
        <div className="table-wrap">
          <div className="empty">Loading…</div>
        </div>
      ) : (
        <>
          {/* ---------- Provider ---------- */}
          <section className="card">
            <div className="card-head">
              <span className="card-title">Vision provider</span>
            </div>
            <p className="hint" style={{ marginTop: 0 }}>
              The model that reads each photo. Select <code>None</code> to disable AI moderation
              entirely.
            </p>

            <div className="form-grid" style={{ marginTop: 12 }}>
              <div className="field">
                <label>Provider</label>
                <select
                  className="input"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as Provider)}
                >
                  {PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <button
                  className="btn"
                  style={{ marginTop: 8, alignSelf: 'flex-start' }}
                  disabled={busyKey === K_PROVIDER}
                  onClick={() => void save(K_PROVIDER, provider, { reloadAfter: true })}
                >
                  {busyKey === K_PROVIDER ? 'Saving…' : savedKey === K_PROVIDER ? 'Saved ✓' : 'Save provider'}
                </button>
                {provider === 'none' && (
                  <span className="hint">
                    AI moderation is off. The checks below have no effect until a provider is set.
                  </span>
                )}
              </div>

              <div className="field">
                <label>Model</label>
                <input
                  className="input"
                  placeholder="e.g. MiniMax-M3, claude-sonnet-4-6, gpt-4o-mini"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
                <span className="hint">
                  Must be a <strong>vision</strong> model (it reads the photo). Low-cost picks:{' '}
                  <code>MiniMax-M3</code> or <code>claude-sonnet-4-6</code>. Image-generation
                  (gpt-image) and code models won’t work.
                </span>
                <button
                  className="btn"
                  style={{ marginTop: 8, alignSelf: 'flex-start' }}
                  disabled={busyKey === K_MODEL}
                  onClick={() => void save(K_MODEL, model)}
                >
                  {busyKey === K_MODEL ? 'Saving…' : savedKey === K_MODEL ? 'Saved ✓' : 'Save model'}
                </button>
              </div>

              {showEndpoint && (
                <div className="field">
                  <label>Endpoint (optional)</label>
                  <input
                    className="input"
                    placeholder={endpointPlaceholder}
                    value={endpoint}
                    onChange={(e) => setEndpoint(e.target.value)}
                  />
                  <span className="hint">{endpointHint}</span>
                  <button
                    className="btn"
                    style={{ marginTop: 8, alignSelf: 'flex-start' }}
                    disabled={busyKey === K_ENDPOINT}
                    onClick={() => void save(K_ENDPOINT, endpoint)}
                  >
                    {busyKey === K_ENDPOINT
                      ? 'Saving…'
                      : savedKey === K_ENDPOINT
                        ? 'Saved ✓'
                        : 'Save endpoint'}
                  </button>
                </div>
              )}

              <div className="field">
                <label>API key</label>
                <span className="hint mono" title="Secret — only the last 4 characters are shown">
                  {apiKeyHint ?? '(not set)'}
                </span>
                <input
                  className="input"
                  type="password"
                  placeholder={apiKeyHint ? 'Replace secret…' : 'Paste API key…'}
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                />
                <button
                  className="btn"
                  style={{ marginTop: 8, alignSelf: 'flex-start' }}
                  disabled={busyKey === K_API_KEY || apiKey === ''}
                  title={apiKey === '' ? 'Type a key first' : undefined}
                  onClick={() =>
                    void save(K_API_KEY, apiKey, { isSecret: true, reloadAfter: true }).then(() =>
                      setApiKey(''),
                    )
                  }
                >
                  {busyKey === K_API_KEY ? 'Saving…' : savedKey === K_API_KEY ? 'Saved ✓' : 'Replace key'}
                </button>
                <span className="hint">Stored server-side; only the last-4 hint ever comes back.</span>
              </div>
            </div>

            {/* Live connectivity test — runs server-side against the saved config. */}
            <div className="field" style={{ marginTop: 18, maxWidth: 520, gap: 8 }}>
              <label>Connection test</label>
              <button
                className="btn"
                style={{ alignSelf: 'flex-start' }}
                disabled={probing || provider === 'none'}
                onClick={() => void runTest()}
              >
                {probing ? 'Testing…' : 'Test connection'}
              </button>
              <span className="hint">
                Sends one small test image to the provider using the <strong>saved</strong> key,
                endpoint and model — so it verifies exactly what moderation will use. The API key
                never leaves the server.
                {providerDirty && ' Save the provider first to test your new selection.'}
              </span>
              {probe && (
                <div
                  style={{
                    borderLeft: `3px solid ${probe.ok ? '#3fb950' : '#e5484d'}`,
                    paddingLeft: 10,
                    marginTop: 4,
                  }}
                >
                  <strong style={{ color: probe.ok ? '#3fb950' : '#e5484d' }}>
                    {probe.ok ? '✓ Working' : '✗ Failed'}
                  </strong>
                  <p style={{ margin: '4px 0', fontSize: '0.84rem' }}>{probe.message}</p>
                  <span className="muted mono" style={{ fontSize: '0.74rem' }}>
                    {[
                      probe.provider && `provider: ${probe.provider}`,
                      probe.model && `model: ${probe.model}`,
                      typeof probe.status === 'number' ? `HTTP ${probe.status}` : '',
                      typeof probe.latencyMs === 'number' ? `${probe.latencyMs} ms` : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  {probe.ok && probe.sample && (
                    <span
                      className="muted"
                      style={{ display: 'block', marginTop: 4, fontSize: '0.74rem' }}
                    >
                      sample read → person: {String(probe.sample.isPerson)}, explicit:{' '}
                      {String(probe.sample.explicit)}, gender: {probe.sample.estimatedGender ?? 'null'}
                    </span>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ---------- Checks ---------- */}
          <section className="card">
            <div className="card-head">
              <span className="card-title">Checks</span>
            </div>
            <p className="hint" style={{ marginTop: 0 }}>
              Each enabled check auto-rejects the upload (the user must replace the photo). Checks
              marked “needs a vision provider” do nothing until a provider + API key is set above.
            </p>
            <div className="form-grid" style={{ marginTop: 12 }}>
              {CHECKS.map((c) => (
                <Toggle
                  key={c.key}
                  label={c.label}
                  hint={c.hint}
                  checked={boolOf(byKey.get(c.key))}
                  busy={busyKey === c.key}
                  onChange={(next) => void save(c.key, next ? 'true' : 'false', { reloadAfter: true })}
                />
              ))}
              <Toggle
                label="Self-hosted NSFW model"
                hint="Runs an in-house model; may be unavailable until provisioned. When off, NSFW uses the vision provider above."
                checked={boolOf(byKey.get(K_NSFW_SELF_HOSTED))}
                busy={busyKey === K_NSFW_SELF_HOSTED}
                onChange={(next) =>
                  void save(K_NSFW_SELF_HOSTED, next ? 'true' : 'false', { reloadAfter: true })
                }
              />
            </div>
          </section>

          {/* ---------- In-house ML (no API) ---------- */}
          <section className="card">
            <div className="card-head">
              <span className="card-title">In-house ML (no API)</span>
            </div>
            <p className="hint" style={{ marginTop: 0 }}>
              Runs face checks locally on the server (TensorFlow.js WASM + face-api) — <strong>no
              external API, no per-photo cost</strong>. Covers real-person, same-person and gender;
              the vision provider above is then used only for AI-generated/deepfake (and as a
              fallback). The ML libraries + model weights ship in the API image — just toggle it on.
              The models folder below is optional (blank = the bundled weights). Falls back to the
              provider if the local stack is unavailable.
            </p>
            <div className="form-grid" style={{ marginTop: 12 }}>
              <Toggle
                label="Enable in-house face vision"
                hint="Prefer local face detection / same-person / gender over the API. Needs deps + models (below)."
                checked={boolOf(byKey.get(K_INHOUSE))}
                busy={busyKey === K_INHOUSE}
                onChange={(next) => void save(K_INHOUSE, next ? 'true' : 'false', { reloadAfter: true })}
              />
              <div className="field">
                <label>Face-api models folder</label>
                <input
                  className="input"
                  placeholder="https://your-host/face-models  or  /app/models"
                  value={inhouseModels}
                  onChange={(e) => setInhouseModels(e.target.value)}
                />
                <span className="hint">
                  URL or path to the face-api weight files (tiny_face_detector, face_landmark_68,
                  face_recognition, age_gender). Blank = in-house face vision stays off.
                </span>
                <button
                  className="btn"
                  style={{ marginTop: 8, alignSelf: 'flex-start' }}
                  disabled={busyKey === K_INHOUSE_MODELS}
                  onClick={() => void save(K_INHOUSE_MODELS, inhouseModels, { reloadAfter: true })}
                >
                  {busyKey === K_INHOUSE_MODELS ? 'Saving…' : savedKey === K_INHOUSE_MODELS ? 'Saved ✓' : 'Save models path'}
                </button>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
